import {
  FORTA_BLACK,
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY1,
  FORTA_GRAY3,
  FORTA_WHITE,
} from '../../../utils/colors';

import type { SxProps, Theme } from '@mui/material/styles';

const TOOLBAR_BUTTON_SIZE = 34;
const TOOLBAR_ICON_SIZE = 17;
const TOOLBAR_BUTTON_GAP = 8;
const TOOLBAR_BOTTOM_GAP = 4;
/** Vertical space reserved above the bordered editor for the floating toolbar row. */
const TOOLBAR_RESERVED_HEIGHT = TOOLBAR_BUTTON_SIZE + TOOLBAR_BOTTOM_GAP;

export function buildQuillLabelSx(error: boolean): SxProps<Theme> {
  return {
    position: 'absolute',
    top: `${TOOLBAR_RESERVED_HEIGHT}px`,
    left: 12,
    transform: 'translateY(-50%)',
    px: 0.5,
    zIndex: 1,
    pointerEvents: 'none',
    maxWidth: 'calc(100% - 24px)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    backgroundColor: 'transparent',
    color: error ? FORTA_ERROR : FORTA_BLACK,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.2,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      height: '3px',
      backgroundColor: FORTA_WHITE,
      zIndex: -1,
    },
  };
}

export function buildQuillEditorSx({
  error,
  disabled,
  minHeight,
}: {
  error: boolean;
  disabled: boolean;
  minHeight: number;
}): SxProps<Theme> {
  const borderColor = error ? FORTA_ERROR : FORTA_BLACK;

  return {
    position: 'relative',
    pt: `${TOOLBAR_RESERVED_HEIGHT}px`,
    '& .ql-toolbar.ql-snow': {
      position: 'absolute',
      top: 0,
      right: 0,
      display: 'flex',
      alignItems: 'center',
      gap: `${TOOLBAR_BUTTON_GAP}px`,
      padding: 0,
      border: 'none',
    },
    '& .ql-toolbar.ql-snow:after': {
      display: 'none',
    },
    '& .ql-toolbar.ql-snow .ql-formats': {
      display: 'contents',
      margin: 0,
    },
    '& .ql-toolbar.ql-snow .ql-formats:after': {
      display: 'none',
    },
    '& .ql-toolbar.ql-snow button, & .ql-toolbar.ql-snow .ql-picker.ql-color': {
      width: `${TOOLBAR_BUTTON_SIZE}px`,
      height: `${TOOLBAR_BUTTON_SIZE}px`,
      minWidth: `${TOOLBAR_BUTTON_SIZE}px`,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      float: 'none',
      flexShrink: 0,
      padding: 0,
      borderRadius: '8px',
      color: FORTA_BLUE.primary,
      border: `1px solid ${FORTA_BLUE.primary}`,
      backgroundColor: FORTA_WHITE,
    },
    '& .ql-toolbar.ql-snow button svg, & .ql-toolbar.ql-snow .ql-picker.ql-color svg, & .ql-toolbar.ql-snow .f-quill-toolbar-icon': {
      width: `${TOOLBAR_ICON_SIZE}px`,
      height: `${TOOLBAR_ICON_SIZE}px`,
      float: 'none',
      position: 'static',
      margin: 0,
    },
    '& .ql-toolbar.ql-snow .ql-picker.ql-color .ql-picker-label': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      padding: 0,
      border: 'none',
    },
    '& .ql-toolbar.ql-snow button:hover, & .ql-toolbar.ql-snow .ql-picker.ql-color:hover': {
      borderColor: FORTA_BLUE.primary,
      backgroundColor: FORTA_BLUE.light3,
    },
    '& .ql-toolbar.ql-snow button': {
      color: FORTA_BLUE.primary,
    },
    '& .ql-toolbar.ql-snow button.ql-template': {
      color: FORTA_BLACK,
    },
    '& .ql-toolbar.ql-snow button.ql-active': {
      borderColor: FORTA_BLUE.primary,
      backgroundColor: FORTA_BLUE.light3,
    },
    '& .ql-toolbar.ql-snow button.ql-active .ql-stroke': {
      stroke: FORTA_BLUE.primary,
    },
    '& .ql-toolbar.ql-snow button.ql-active .ql-fill': {
      fill: FORTA_BLUE.primary,
    },
    '& .ql-toolbar.ql-snow button:disabled, & .ql-toolbar.ql-snow .ql-picker.ql-color.ql-disabled': {
      cursor: 'not-allowed',
      opacity: 0.45,
    },
    '& .ql-container.ql-snow, & .ql-toolbar.ql-snow + .ql-container.ql-snow': {
      border: `1px solid ${borderColor}`,
      borderRadius: '12px',
      fontFamily: 'DMSansRegular, sans-serif',
      fontSize: 16,
      backgroundColor: disabled ? FORTA_GRAY1 : FORTA_WHITE,
    },
    '& .ql-editor': {
      minHeight,
      padding: '14px 16px',
      color: FORTA_BLACK,
      fontFamily: 'DMSansRegular, sans-serif',
      fontSize: 16,
    },
    '& .ql-editor.ql-blank::before': {
      left: '16px',
      right: '16px',
      color: FORTA_GRAY3,
      fontStyle: 'normal',
    },
  };
}

export function buildQuillHelperTextSx(error: boolean): SxProps<Theme> {
  return {
    color: error ? FORTA_ERROR : FORTA_GRAY3,
    fontSize: 12,
    mt: 0.5,
  };
}
